/**
 * CE QUE LE POSTE GARDE D'UN CONTENU : le BROUILLON et la COPIE DE SECOURS.
 * Pur (le stockage est injecte), pour se prouver sans navigateur
 * (`tests/sauvegarde-poste.test.mjs`).
 *
 * DEUX TIROIRS, ET LES CONFONDRE A COUTE CHER A LA PR 212 :
 *
 *  - le BROUILLON rattrape ce que le reseau n'a pas eu le temps d'emporter
 *    (une page rechargee pendant une ecriture). Il porte la revision sur
 *    laquelle il a ete fabrique (`versionBase`) : s'il n'est pas en retard sur
 *    la base, on le REPREND et on l'ecrit, sans rien demander ;
 *  - la COPIE DE SECOURS garde un travail qu'on ne peut PAS ecrire en place :
 *    quelqu'un a enregistre entre-temps (conflit), le contenu a ete archive ou
 *    supprime, les droits sont tombes, le format a change. Elle ne se reprend
 *    JAMAIS seule : elle s'annonce, se telecharge, se restaure en un contenu
 *    neuf, ou se jette, par un geste de la personne.
 *
 * La PR 212 n'avait que le premier tiroir, et un brouillon en retard sur la base
 * y etait JETE sans un mot (« le plus recent gagne ») : les gestes d'une
 * personne disparaissaient parce qu'une autre avait enregistre avant elle.
 * Ici, un brouillon en retard devient une copie de secours (`reprise.ts`).
 *
 * ISOLES PAR PROJET, ORG ET COMPTE. La clef de la PR 212 ne portait que l'id du
 * contenu : sur un navigateur partage, le compte suivant reprenait et
 * ENREGISTRAIT les gestes non publies du precedent, sous son nom. La clef porte
 * maintenant le projet Firebase, l'org et l'uid ; sans compte connu, rien ne se
 * pose (`identite` absente), et l'ecran le dit.
 *
 * UN REFUS DU STOCKAGE EST UN RESULTAT, PAS UN SILENCE. `poser` rend `ok` ou la
 * raison (`plein`, `interdit`, `absent`) ; l'appelant la montre et, tant qu'un
 * travail n'est nulle part ailleurs, retient la page a la fermeture.
 */

/** Le stockage du poste, reduit a ce qu'on en utilise (`localStorage` en vrai). */
export interface StockageDuPoste {
  getItem(clef: string): string | null;
  setItem(clef: string, valeur: string): void;
  removeItem(clef: string): void;
}

/** A qui appartient ce que le poste garde : le projet Firebase, l'org, le compte. */
export interface IdentiteDuPoste {
  projet: string;
  org: string;
  compte: string;
}

/** L'ecriture qui etait en vol quand le brouillon a ete pose (cf. `brouillonEnAvance`). */
export interface EcritureEnVol {
  version: number;
  empreinte: string;
}

/** Le brouillon : le texte au format du code qui l'a pose, et sa revision de depart. */
export interface Brouillon {
  versionBase: number;
  formatVersion: number;
  texte: string;
  /** `Date.now()` a la pose. */
  pose: number;
  enVol?: EcritureEnVol;
  /**
   * L'ONGLET qui l'a pose. Deux onglets du meme compte sur le meme contenu
   * partagent la clef : un onglet qui vient d'ecrire n'efface que SON
   * brouillon, jamais celui que l'autre a pose pour son ecriture en vol.
   */
  onglet?: string;
}

/** Pourquoi une copie de secours a ete mise de cote. */
export type MotifDeCopie = 'conflit' | 'archive' | 'supprime' | 'droits' | 'format' | 'introuvable';

/** La copie de secours : un travail qui ne s'ecrit pas en place, garde pour la personne. */
export interface CopieDeSecours {
  versionBase: number;
  formatVersion: number;
  texte: string;
  pose: number;
  motif: MotifDeCopie;
  /** Le nom du contenu a ce moment-la, pour nommer ce qu'on restaure ou telecharge. */
  nom: string;
}

/** Ce que rend une pose : `ok`, ou pourquoi le poste a refuse. */
export type ResultatDePose = { ok: true } | { ok: false; raison: 'absent' | 'plein' | 'interdit' };

/** Un brouillon plus vieux n'est plus propose : il est jete a la lecture. */
export const PEREMPTION_BROUILLON_MS = 14 * 24 * 60 * 60 * 1000;
/** Une copie de secours vit plus longtemps : c'est un travail qu'on n'a pas su ecrire. */
export const PEREMPTION_COPIE_MS = 60 * 24 * 60 * 60 * 1000;

const PREFIXE = 'taki.contenu.v2';

/** Un morceau de clef sans `.` ni caractere ambigu (un uid, une org, un id). */
function morceauDeClef(valeur: string): string {
  return encodeURIComponent(valeur).replace(/\./g, '%2E');
}

/** La clef d'un tiroir, pour un contenu et une identite. */
export function clefDuPoste(tiroir: 'brouillon' | 'copie', identite: IdentiteDuPoste, contentId: string): string {
  return [
    PREFIXE,
    tiroir,
    morceauDeClef(identite.projet),
    morceauDeClef(identite.org),
    morceauDeClef(identite.compte),
    morceauDeClef(contentId),
  ].join('.');
}

/**
 * L'EMPREINTE D'UN TEXTE : sa longueur et un hachage FNV-1a 32 bits. Pure et
 * synchrone (elle sert dans `beforeunload`). Elle reconnait NOTRE ecriture
 * parmi celles qui ont pu passer ; elle ne protege de rien de malveillant.
 */
export function empreinteDeTexte(texte: string): string {
  let hachage = 0x811c9dc5;
  for (let i = 0; i < texte.length; i += 1) {
    hachage ^= texte.charCodeAt(i);
    hachage = Math.imul(hachage, 0x01000193);
  }
  return `${texte.length}:${(hachage >>> 0).toString(16)}`;
}

/** La raison d'un refus de `setItem`, lue sur l'erreur du navigateur. */
export function raisonDuRefus(cause: unknown): 'plein' | 'interdit' {
  const { name, code } = (typeof cause === 'object' && cause !== null ? cause : {}) as {
    name?: unknown;
    code?: unknown;
  };
  // 22 : Chrome, Safari ; 1014 : ancien Firefox.
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || code === 22 || code === 1014) {
    return 'plein';
  }
  return 'interdit';
}

function poserBrut(stockage: StockageDuPoste | null, clef: string, valeur: unknown): ResultatDePose {
  if (stockage === null) return { ok: false, raison: 'absent' };
  try {
    stockage.setItem(clef, JSON.stringify(valeur));
    return { ok: true };
  } catch (cause) {
    return { ok: false, raison: raisonDuRefus(cause) };
  }
}

function lireBrut(stockage: StockageDuPoste | null, clef: string): unknown {
  if (stockage === null) return null;
  let brut: string | null;
  try {
    brut = stockage.getItem(clef);
  } catch {
    return null;
  }
  if (brut === null) return null;
  try {
    return JSON.parse(brut) as unknown;
  } catch {
    return undefined;
  }
}

function oublierBrut(stockage: StockageDuPoste | null, clef: string): void {
  try {
    stockage?.removeItem(clef);
  } catch {
    // Un tiroir qu'on n'arrive pas a vider sera ignore par sa revision, ou perimera.
  }
}

const estNombre = (valeur: unknown): valeur is number => typeof valeur === 'number' && Number.isFinite(valeur);

/**
 * LIT UN BROUILLON BRUT, pure. `aJeter` : illisible, mal forme ou perime. Un
 * brouillon d'avant la version du format (`formatVersion` absent) est lu au
 * format 1, le seul qui existait alors.
 */
export function analyserBrouillon(lu: unknown, maintenant: number): { brouillon: Brouillon | null; aJeter: boolean } {
  if (lu === null) return { brouillon: null, aJeter: false };
  if (typeof lu !== 'object' || lu === undefined) return { brouillon: null, aJeter: true };
  const c = lu as Partial<Brouillon>;
  if (!estNombre(c.versionBase) || typeof c.texte !== 'string' || !estNombre(c.pose)) {
    return { brouillon: null, aJeter: true };
  }
  if (maintenant - c.pose > PEREMPTION_BROUILLON_MS) return { brouillon: null, aJeter: true };
  const brouillon: Brouillon = {
    versionBase: c.versionBase,
    formatVersion: estNombre(c.formatVersion) ? c.formatVersion : 1,
    texte: c.texte,
    pose: c.pose,
  };
  const enVol = c.enVol as Partial<EcritureEnVol> | undefined;
  if (estNombre(enVol?.version) && typeof enVol.empreinte === 'string') {
    brouillon.enVol = { version: enVol.version, empreinte: enVol.empreinte };
  }
  if (typeof c.onglet === 'string') brouillon.onglet = c.onglet;
  return { brouillon, aJeter: false };
}

const MOTIFS: readonly MotifDeCopie[] = ['conflit', 'archive', 'supprime', 'droits', 'format', 'introuvable'];

/** LIT UNE COPIE DE SECOURS BRUTE, pure (meme contrat que `analyserBrouillon`). */
export function analyserCopie(lu: unknown, maintenant: number): { copie: CopieDeSecours | null; aJeter: boolean } {
  if (lu === null) return { copie: null, aJeter: false };
  if (typeof lu !== 'object' || lu === undefined) return { copie: null, aJeter: true };
  const c = lu as Partial<CopieDeSecours>;
  if (
    !estNombre(c.versionBase) ||
    !estNombre(c.formatVersion) ||
    typeof c.texte !== 'string' ||
    !estNombre(c.pose) ||
    !MOTIFS.includes(c.motif as MotifDeCopie)
  ) {
    return { copie: null, aJeter: true };
  }
  if (maintenant - c.pose > PEREMPTION_COPIE_MS) return { copie: null, aJeter: true };
  return {
    copie: {
      versionBase: c.versionBase,
      formatVersion: c.formatVersion,
      texte: c.texte,
      pose: c.pose,
      motif: c.motif as MotifDeCopie,
      nom: typeof c.nom === 'string' ? c.nom : '',
    },
    aJeter: false,
  };
}

/**
 * LES DEUX TIROIRS D'UN CONTENU POUR UNE IDENTITE. Sans identite (compte
 * inconnu) ou sans stockage (navigation privee stricte, hors navigateur), tout
 * rend `absent` : rien ne se pose, rien ne se lit, et l'appelant le dit.
 */
export interface TiroirsDuPoste {
  readonly disponible: boolean;
  poserBrouillon(brouillon: Omit<Brouillon, 'pose'>): ResultatDePose;
  lireBrouillon(): Brouillon | null;
  /**
   * Oublie le brouillon. Par defaut, seulement s'il est de CET onglet (`onglet`
   * de `tiroirsDuPoste`) ; `tous` l'oublie quel qu'il soit (a l'ouverture,
   * quand on vient de le reprendre ou de le mettre de cote).
   */
  oublierBrouillon(tous?: boolean): void;
  poserCopie(copie: Omit<CopieDeSecours, 'pose'>): ResultatDePose;
  lireCopie(): CopieDeSecours | null;
  oublierCopie(): void;
}

export function tiroirsDuPoste(
  stockage: StockageDuPoste | null,
  identite: IdentiteDuPoste | null,
  contentId: string,
  onglet: string,
  maintenant: () => number = Date.now,
): TiroirsDuPoste {
  const utilisable = stockage !== null && identite !== null;
  const clefB = identite === null ? '' : clefDuPoste('brouillon', identite, contentId);
  const clefC = identite === null ? '' : clefDuPoste('copie', identite, contentId);
  const sto = utilisable ? stockage : null;
  return {
    disponible: utilisable,
    poserBrouillon: (brouillon) => poserBrut(sto, clefB, { ...brouillon, onglet, pose: maintenant() }),
    lireBrouillon: () => {
      const { brouillon, aJeter } = analyserBrouillon(lireBrut(sto, clefB), maintenant());
      if (aJeter) oublierBrut(sto, clefB);
      return brouillon;
    },
    oublierBrouillon: (tous = false) => {
      if (!tous) {
        const lu = lireBrut(sto, clefB);
        const autre =
          typeof lu === 'object' && lu !== null && typeof (lu as { onglet?: unknown }).onglet === 'string'
            ? (lu as { onglet: string }).onglet !== onglet
            : false;
        if (autre) return;
      }
      oublierBrut(sto, clefB);
    },
    poserCopie: (copie) => poserBrut(sto, clefC, { ...copie, pose: maintenant() }),
    lireCopie: () => {
      const { copie, aJeter } = analyserCopie(lireBrut(sto, clefC), maintenant());
      if (aJeter) oublierBrut(sto, clefC);
      return copie;
    },
    oublierCopie: () => oublierBrut(sto, clefC),
  };
}

/** Le `localStorage` du navigateur, ou `null` (hors navigateur, ou interdit). */
export function stockageDuNavigateur(): StockageDuPoste | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
